import React from "react";
import { useSelector } from "react-redux";
import { formatInTimeZone } from "date-fns-tz";
import {
  Box, Stack, Typography, Chip, Divider,
  Accordion, AccordionSummary, AccordionDetails,
  useTheme, useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import { LAYOUT_HEIGHT } from "state/constants";
import CustomDataGridToolbar from "components/CustomDataGridToolbar";


function LegRow({ flight, timeMode, theme }) {
    const pickup = timeMode === "UTC"
        ? flight.fullPickup && formatInTimeZone(flight.fullPickup, "UTC", "HH:mm")
        : flight.fullPickupLocalBase && formatInTimeZone(flight.fullPickupLocalBase, "UTC", "HH:mm");

    const takeBack = timeMode === "UTC"
        ? flight.fullTakeBack && formatInTimeZone(flight.fullTakeBack, "UTC", "HH:mm")
        : flight.fullTakeBackLocalBase && formatInTimeZone(flight.fullTakeBackLocalBase, "UTC", "HH:mm");

    const atd = timeMode === "UTC"
        ? (flight?.atd || flight?.etd)
        : (flight?.atdLocalBase || flight?.etdLocalBase);

    const ata = timeMode === "UTC"
        ? (flight?.ata || flight?.eta)
        : (flight?.ataLocalBase || flight?.etaLocalBase);

    return (
        <Box sx={{ py: 0.75, borderBottom: `1px solid ${theme.palette.divider}`, "&:last-child": { borderBottom: 0 } }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={flight.flightNo} variant="outlined" />
                <Typography variant="body2" fontWeight={600}>
                    {flight.dep} → {flight.arr}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {atd}{ata ? ` – ${ata}` : ""}
                </Typography>
                {flight.pax && (
                    <Typography variant="caption" color="text.secondary">PAX</Typography>
                )}
            </Stack>
            {(pickup || takeBack) && (
                <Stack direction="row" spacing={2} mt={0.5} flexWrap="wrap" useFlexGap>
                    {pickup && (
                        <Typography variant="caption" sx={{ color: "primary.main" }}>
                            ↑ {pickup}  {flight.pickupDep}-{flight.pickupArr}
                        </Typography>
                    )}
                    {takeBack && (
                        <Typography variant="caption" sx={{ color: "secondary.main" }}>
                            ↓ {takeBack}  {flight.takeBackDep}-{flight.takeBackArr}
                        </Typography>
                    )}
                </Stack>
            )}
        </Box>
    );
}

function CrewRow({ member, theme }) {
    return (
        <Stack direction="row" spacing={1} alignItems="center" py={0.25} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: "right" }}>
                {member.crewCode}
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: member.gender === "FM" ? theme.palette.secondary.main : theme.palette.primary.dark }}
            >
                {member.crewName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                ({member.post} | {member.base}{member.trnDuty ? ` | ${member.trnDuty}` : ""})
            </Typography>
        </Stack>
    );
}

function RouteAccordion({ route, timeMode, theme }) {
    const firstLeg = route.routeDetails?.[0];
    const lastLeg  = route.routeDetails?.[route.routeDetails.length - 1];

    const firstPickup = timeMode === "UTC"
        ? firstLeg?.fullPickup && formatInTimeZone(firstLeg.fullPickup, "UTC", "HH:mm")
        : firstLeg?.fullPickupLocalBase && formatInTimeZone(firstLeg.fullPickupLocalBase, "UTC", "HH:mm");

    const lastTakeBack = timeMode === "UTC"
        ? lastLeg?.fullTakeBack && formatInTimeZone(lastLeg.fullTakeBack, "UTC", "HH:mm")
        : lastLeg?.fullTakeBackLocalBase && formatInTimeZone(lastLeg.fullTakeBackLocalBase, "UTC", "HH:mm");

    const dateStr = route.routeDate
        ? formatInTimeZone(
            timeMode === "UTC" ? route.routeDate : (route.routeDateLocalBase || route.routeDate),
            "UTC", "dd/MM"
        )
        : null;

    const sortedCrew = [...(route.crewMembers || [])].sort((a, b) => a.rankPriority - b.rankPriority);

    return (
        <Accordion
            disableGutters
            elevation={0}
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                mb: 1,
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ minHeight: 0, py: 0.5, "& .MuiAccordionSummary-content": { my: 0.5 } }}
            >
                <Stack spacing={0.25} flex={1} mr={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={600}>
                                {route.crewRoute || route.routeNo}
                            </Typography>
                            {dateStr && (
                                <Typography variant="caption" color="text.secondary">{dateStr}</Typography>
                            )}
                        </Stack>
                        <Chip size="small" label={`${route.crewCount} crew`} />
                    </Stack>
                    {(firstPickup || lastTakeBack) && (
                        <Stack direction="row" spacing={2}>
                            {firstPickup && (
                                <Typography variant="caption" sx={{ color: "primary.main" }}>
                                    ↑ {firstPickup}
                                </Typography>
                            )}
                            {lastTakeBack && (
                                <Typography variant="caption" sx={{ color: "secondary.main" }}>
                                    ↓ {lastTakeBack}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
                <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                    Sectors
                </Typography>
                {route.routeDetails.map((flight, i) => (
                    <LegRow key={i} flight={flight} timeMode={timeMode} theme={theme} />
                ))}

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                    Crew
                </Typography>
                {sortedCrew.map((member, i) => (
                    <CrewRow key={i} member={member} theme={theme} />
                ))}
            </AccordionDetails>
        </Accordion>
    );
}


const ShuttleTable = ({ crewShuttles, columns, loading }) => {
  const theme    = useTheme();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const timeMode = useSelector((state) => state.timemode);

  if (isMobile) {
    return (
      <Box sx={{ height: LAYOUT_HEIGHT, overflowY: "auto", py: 1, px: 0.5 }}>
        {!crewShuttles?.length ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" mt={4}>
            No shuttles found.
          </Typography>
        ) : (
          crewShuttles.map((route) => (
            <RouteAccordion
              key={route._id}
              route={route}
              timeMode={timeMode}
              theme={theme}
            />
          ))
        )}
      </Box>
    );
  }

  return (
    <Box display="grid" gap={2} gridTemplateColumns="repeat(12, minmax(0, 1fr))">
      <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>
        <DataGrid
          getRowId={(row) => row._id}
          rows={crewShuttles || []}
          columns={columns}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          loading={loading}
          showToolbar
          slots={{ toolbar: () => <CustomDataGridToolbar /> }}
        />
      </Box>
    </Box>
  );
};

export default ShuttleTable;
